import { useMemo, useState, type ReactNode } from "react";
import { Check, ChevronDown, ChevronRight, Clipboard, Loader2, RotateCcw, Play, FlaskConical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import type { SmbAdminStep } from "@shared/smb-admin";
import { EnrichmentControls } from "./EnrichmentControls";
import { GenerationReviewControls } from "./GenerationReviewControls";
import { HandoffInspection } from "./HandoffInspection";
import { WeeklyStatics } from "./WeeklyStatics";
import { PublishControls } from "./PublishControls";

type Props = {
  step: SmbAdminStep;
  orderId: string;
  clientName?: string;
  isTest: boolean;
  expanded: boolean;
  onToggle: () => void;
  onRun: (key: string, dryRun: boolean) => void;
  onRewind: (key: string) => void;
  busy: boolean;
  children?: ReactNode;
};

const pretty = (value: unknown) => JSON.stringify(value, null, 2);
function statusTone(status?: string | null) {
  if (status === "failed") return "text-destructive";
  return "text-muted-foreground";
}

export function StepCard({ step, orderId, clientName, isTest, expanded, onToggle, onRun, onRewind, busy, children }: Props) {
  const [copied, setCopied] = useState(false);
  const state = step.state;
  const actionBusy = busy || state?.status === "running";
  const recentEvent = step.events[0];
  const result = recentEvent?.type === "step_run" && recentEvent.detailJson.dryRun === true
    ? recentEvent.detailJson.result : state?.result ?? step.description;
  const lastRun = state?.completedAt ?? state?.startedAt ?? recentEvent?.at;
  const deepLink = `${window.location.origin}/admin/smb/orders/${encodeURIComponent(orderId)}/${step.key}`;
  const output = useMemo(() => result == null ? "" : pretty(result), [result]);

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(deepLink);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  }
  return (
    <section id={`smb-step-${step.key}`} className={`overflow-hidden rounded-xl border transition-colors ${expanded ? "border-primary/30 bg-card shadow-sm" : "border-border bg-card/70"}`}>
      <button type="button" onClick={onToggle} aria-expanded={expanded} className="flex w-full items-center gap-3 p-4 text-left hover:bg-muted/35 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
        <span className={`grid h-8 w-8 shrink-0 place-items-center rounded-full border text-xs font-semibold tabular-nums ${state?.status === "completed" ? "border-primary/20 bg-primary/10 text-primary" : "border-border bg-muted/60 text-muted-foreground"}`}>
          {state?.status === "completed" ? <Check className="h-4 w-4" /> : String(step.order + 1).padStart(2, "0")}
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-foreground">{step.label}</span>
            <Badge variant="outline" className={`h-5 rounded-md px-1.5 text-[10px] font-medium capitalize ${statusTone(state?.status)}`}>{state?.status ?? "not started"}</Badge>
            {!step.registered && <span className="text-[10px] uppercase tracking-[.12em] text-muted-foreground">Not registered</span>}
          </span>
          <span className="mt-1 block text-xs text-muted-foreground">{lastRun ? `Last activity ${new Date(lastRun).toLocaleString()}` : "No run recorded"}</span>
        </span>
        {expanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
      </button>
      {expanded && (
        <div className="border-t border-border/70">
          <div className="min-h-16 px-4 py-4">
            {children}
            {step.key === "enrichment" && step.registered &&
              <EnrichmentControls step={step} orderId={orderId} busy={actionBusy} onRun={onRun} />}
            {step.key === "generation" && <WeeklyStatics orderId={orderId} clientName={clientName} />}
            {(step.key === "generation" || step.key === "review") && <GenerationReviewControls orderId={orderId} />}
            {step.key === "handoff" && <HandoffInspection orderId={orderId} busy={actionBusy} onRun={onRun} />}
            {step.key === "publish" && <PublishControls orderId={orderId} busy={actionBusy} onRun={onRun} />}
            {step.key !== "generation" && step.key !== "review" && step.key !== "handoff" && step.key !== "publish" && <p className="text-sm text-muted-foreground">{step.registered ? "Step is available to run." : "This step is a placeholder. It will become runnable when a service registers it."}</p>}
            {step.events.length > 0 && <div className="mt-4 space-y-2">
              <h3 className="text-[10px] font-semibold uppercase tracking-[.14em] text-muted-foreground">Recent events</h3>
              {step.events.slice(0, 4).map((event) => (
                <div key={event.id} className="flex items-start justify-between gap-3 rounded-lg bg-muted/40 px-3 py-2 text-xs">
                  <span className="min-w-0 break-words font-medium">{event.type}</span>
                  <time className="shrink-0 text-muted-foreground">{new Date(event.at).toLocaleString()}</time>
                  <pre className="col-span-2 max-w-full overflow-x-auto whitespace-pre-wrap text-[11px] text-muted-foreground">{pretty(event.detailJson)}</pre>
                </div>
              ))}
            </div>}
          </div>
          <div className="flex flex-wrap items-center gap-2 border-t border-border/70 bg-muted/25 px-4 py-3">
            <Button size="sm" variant="outline" onClick={() => onRun(step.key, false)} disabled={!step.registered || actionBusy}>
              {actionBusy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Play className="mr-1.5 h-3.5 w-3.5" />}
              {state?.status === "completed" ? "Re-run" : "Run"}
            </Button>
            {step.supportsDryRun && <Button size="sm" variant="outline" onClick={() => onRun(step.key, true)} disabled={!step.registered || actionBusy}>
              <FlaskConical className="mr-1.5 h-3.5 w-3.5" />Dry run
            </Button>}
            {isTest && <Button size="sm" variant="outline" onClick={() => onRewind(step.key)} disabled={!step.registered || !step.supportsRewind || actionBusy}>
              <RotateCcw className="mr-1.5 h-3.5 w-3.5" />Rewind to here
            </Button>}
            <Button size="sm" variant="ghost" onClick={() => void copyLink()}><Clipboard className="mr-1.5 h-3.5 w-3.5" />{copied ? "Copied" : "Copy link"}</Button>
          </div>
          <div className="border-t border-border/70 px-4 py-4">
            <h3 className="mb-2 text-[10px] font-semibold uppercase tracking-[.14em] text-muted-foreground">Output</h3>
            {output ? <pre className="max-h-72 overflow-auto rounded-lg bg-muted p-3 font-mono text-[11px] leading-relaxed text-foreground">{output}</pre> : <div className="rounded-lg border border-dashed border-border px-3 py-4 text-xs text-muted-foreground">No output yet</div>}
          </div>
        </div>
      )}
    </section>
  );
}
